// Client wrapper over the `generate-recipe-narration` edge function.
//
// Admin-only — the edge function rejects non-admin callers via the
// is_admin() RPC. The button that calls this should be UI-gated on
// `profile.role === "admin"` (UX) but the security check is server-
// side (correctness).
//
// Cost: ~$0.10-0.30 per recipe in ElevenLabs charges. Triggered only
// by explicit admin action — never auto-fired.

import { supabase } from "./supabase";

/**
 * Generate ElevenLabs narration for every step of a recipe (plus an
 * optional intro). Stamps the public-URL pointers onto
 * `user_recipes.recipe.audio` and returns the audio block.
 *
 * @param {object} args
 *   @param {string} args.recipeId — user_recipes.id (uuid)
 *
 * @returns {Promise<{ audio?: object, error?: string }>}
 *   On success, audio is `{ intro: {url, durationMs}, steps: { id: {url, ...} }, voiceId, generatedAt, generatedBy }`.
 *   On failure, error is a user-readable string.
 */
export async function generateRecipeNarration({ recipeId, chunkKey } = {}) {
  if (!recipeId) {
    return { error: "Need a recipe id." };
  }
  // Optional `chunkKey` ("intro" | "step1" | "step2" | …) re-bakes
  // ONLY that slot and merges into existing recipe.audio. Saves the
  // full-recipe ElevenLabs cost when one chunk came out wrong. When
  // omitted, the full recipe re-bakes and overwrites the whole
  // audio block (existing behavior).
  const body = { recipeId };
  if (typeof chunkKey === "string" && chunkKey.trim()) {
    body.chunkKey = chunkKey.trim();
  }

  const { data, error } = await supabase.functions.invoke("generate-recipe-narration", {
    body,
  });

  if (error) {
    let detail = error.message || "unknown error";
    try {
      const res = error?.context;
      if (res && typeof res.json === "function") {
        const body = await res.json();
        if (body?.error) detail = body.error;
      }
    } catch { /* fall through */ }
    return { error: detail };
  }

  if (!data?.audio) {
    return { error: "Response missing audio block — try again." };
  }
  return { audio: data.audio };
}
